import { queryOptions } from '@tanstack/react-query';

import type { TradeWorkbenchIntervalT } from '../types';
import {
  readMarketChartBars,
  readMarketDefinition,
  readMarketLiquidityHeatmap,
  readMarketOrderBook,
  readMarketSummary,
  readMarkets,
  readMarketTrades,
} from './marketFeedData';

const MARKET_FEED_FALLBACK_STALE_TIME = 10_000;

const MARKET_FEED_FALLBACK_DEFAULTS = {
  refetchOnMount: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
  staleTime: MARKET_FEED_FALLBACK_STALE_TIME,
} as const;

export function marketsQueryOptions() {
  return queryOptions({
    queryFn: ({ signal }) => readMarkets({ signal }),
    queryKey: ['market-feed', 'markets'] as const,
    staleTime: 30_000,
  });
}

export function marketDefinitionQueryOptions(asset: string) {
  return queryOptions({
    queryFn: ({ signal }) => readMarketDefinition({ asset, signal }),
    queryKey: ['market-feed', 'market', asset] as const,
    staleTime: 30_000,
  });
}

export function marketSummaryQueryOptions(asset: string) {
  return queryOptions({
    ...MARKET_FEED_FALLBACK_DEFAULTS,
    queryFn: ({ signal }) => readMarketSummary({ asset, signal }),
    queryKey: ['market-feed', 'summary', asset] as const,
  });
}

export function marketChartBarsQueryOptions(
  asset: string,
  chartInterval: TradeWorkbenchIntervalT,
) {
  return queryOptions({
    ...MARKET_FEED_FALLBACK_DEFAULTS,
    queryFn: ({ signal }) =>
      readMarketChartBars({ asset, chartInterval, signal }),
    queryKey: ['market-feed', 'chart', asset, chartInterval] as const,
  });
}

export function marketTradesQueryOptions(asset: string) {
  return queryOptions({
    ...MARKET_FEED_FALLBACK_DEFAULTS,
    queryFn: ({ signal }) => readMarketTrades({ asset, signal }),
    queryKey: ['market-feed', 'trades', asset] as const,
  });
}

export function marketOrderBookQueryOptions(asset: string, grouping: number) {
  return queryOptions({
    ...MARKET_FEED_FALLBACK_DEFAULTS,
    queryFn: ({ signal }) => readMarketOrderBook({ asset, grouping, signal }),
    queryKey: ['market-feed', 'order-book', asset, grouping] as const,
  });
}

export function marketLiquidityHeatmapQueryOptions(
  asset: string,
  windowMinutes: number,
) {
  return queryOptions({
    ...MARKET_FEED_FALLBACK_DEFAULTS,
    queryFn: ({ signal }) =>
      readMarketLiquidityHeatmap({
        asset,
        signal,
        windowMinutes,
      }),
    queryKey: [
      'market-feed',
      'liquidity-heatmap',
      asset,
      windowMinutes,
    ] as const,
  });
}
